import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { collectionListQueryOptions } from '@/hooks/collections/use-collections';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { RespondedType, ResponseError } from '@/models/response';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { nameSchema } from '@/utils/schemas';

export default function useAddCollection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, AddCollectionRequest>({
    mutationKey: ['/AddCollect'],
    mutationFn: (params) => fetcher('/AddCollect', params, { validResponseCodes: [0] }),
    onSuccess: () => queryClient.invalidateQueries(collectionListQueryOptions),
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data ? t('conversation.save-success') : error?.message,
      }),
  });
}

export const addCollectionRequestScheme = z.object({
  FromID: z.number(), // group id or id of the user who sent the message
  FromName: nameSchema, // group name or name of the user who sent the message
  FromType: z.enum(['friend', 'group']),
  Type: z.nativeEnum(ConversationMessageContentType),
  Content: z.string(),
  SmallPicturePath: z.string().optional(),
});

export type AddCollectionRequest = z.infer<typeof addCollectionRequestScheme>;
